


import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Input, Modal } from 'antd';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useCookies } from 'react-cookie';
import moment from 'moment';
import useNotification from '../../../hooks/layout/useNotification';
import { SET_CURRENT_USER, useAuth } from '../../../hooks/auth/AuthContext';
import { REQUEST_OTP, SUBMIT_OTP, SUBMIT_OTP_CREATE_PHONE } from '../Profile.gql';
import { GET_USER_PROFILE } from '../../login/Login.gql';
import { deleteGeneralCookie, setGeneralCookie } from '../../../shared/helpers/set-general-cookie.helper';
import PhoneInput from '../../../shared/components/phone-input/PhoneInput';
import { CloseCircleOutlined } from '@ant-design/icons';
import '../Profile.less';

interface Props {
    modalVisible: { visible: boolean, setVisible: Dispatch<SetStateAction<boolean>> };
    firstSendCode: any;
    setFirstSendCode: any;
    phoneNumber: any;
    isAddPhone: boolean;
    mainPhoneNumber: string;
    userPhonesRefetch: any;
}

const ProfilePhoneModal = ({ modalVisible, firstSendCode, setFirstSendCode, phoneNumber, isAddPhone, mainPhoneNumber, userPhonesRefetch }: Props) => {
    const intl = useIntl();
    const { setErrorNotification } = useNotification();
    const { authDispatch } = useAuth();
    const [form] = Form.useForm();
    const [phoneNum, setPhoneNum] = useState();
    const [cookies, setCookie, removeCookie] = useCookies();
    const [cooldown, setCooldown] = useState<number>(0);
    const [cooldownTimer, setCooldownTimer] = useState<number>();
    const [countdownStart, setCountdownStart] = useState<number>(299);
    const [minutes, setMinutes] = useState<number>(0);
    const [seconds, setSeconds] = useState<number>(0);
    const [requestOTPCode, { data: requestOTPCodeData }] =
        useMutation(REQUEST_OTP, {
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        });
    const [submitOTP, { data: submitOTPData }] =
        useMutation(SUBMIT_OTP, {
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        });
    const [submitOTPCreatePhone, { data: submitOTPCreatePhoneData }] =
        useMutation(SUBMIT_OTP_CREATE_PHONE, {
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        });
    const [getUserProfile, { data }] = useLazyQuery(GET_USER_PROFILE, {
        fetchPolicy: 'no-cache',
        errorPolicy: "all",
        //notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if (phoneNumber) {
            setPhoneNum(phoneNumber);
        }
    }, [phoneNumber])

    useEffect(() => {
        const cooldownFromCookie = cookies[`c-cooldown-otp`];
        const cooldownTIme = cookies[`c-cooldown-start-otp`] ? +cookies[`c-cooldown-start-otp`] : 299;

        setCountdownStart(+cooldownTIme);

        if (cooldownFromCookie) {
            const timePassed = moment().unix() - (+cooldownFromCookie);
            //due to latency, reduce 1 sec
            const remainingSeconds = timePassed > +cooldownTIme ? 0 : (+cooldownTIme - timePassed);
            setCooldown(remainingSeconds);
            initiateTimer(remainingSeconds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initiateTimer = (amount: number) => {
        const cooldownInterval: number = window.setInterval(() => {
            if (amount > 0) {
                setMinutes(Math.floor(amount / 60));
                setSeconds(amount % 60);
                setCooldown(amount--);
            } else {
                setCooldown(0);
                clearInterval(cooldownInterval);
            }
        }, 1000);
        setCooldownTimer(cooldownInterval);
    }

    useEffect(() => {
        const clearTimer = () => {
            removeCookie(`c-cooldown-otp`);
            if (cooldownTimer) {
                clearInterval(cooldownTimer);
            }
        }
        return () => clearTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cooldownTimer])

    const selectCountryId = (countryId: string) => {
        //setCountryId(countryId);
    }

    const onFinish = (fieldsValue: any) => {
        setFirstSendCode(true);

        if (!isAddPhone) {
            submitOTP({
                variables: {
                    verificationType: 'PHONE',
                    verificationCode: fieldsValue.otpCode,
                    phoneNumber: phoneNum
                },
            });
        } else {
            submitOTPCreatePhone({
                variables: {
                    verificationType: 'PHONE',
                    verificationCode: fieldsValue.otpCode,
                    phoneNumber: mainPhoneNumber,
                    newPhoneNumber: fieldsValue.phone
                },
            });
        }
    }

    const onSendCodeClick = (firstSend: boolean) => {
        form.validateFields();

        const phone = form.getFieldValue('phone');
        if (phone) {
            setGeneralCookie(`c-cooldown-otp`, moment().unix().toString(), countdownStart);
            setMinutes(Math.floor(countdownStart / 60));
            setSeconds(countdownStart % 60);
            setCooldown(countdownStart);
            if (cooldownTimer) {
                clearInterval(cooldownTimer);
            }
            initiateTimer(countdownStart);
            requestOTPCode({
                variables: {
                    verificationType: 'PHONE',
                    //phoneNumber: isAddPhone ? mainPhoneNumber : phoneNum
                },
            });
            setFirstSendCode(!firstSend);
        }
    }

    useEffect(() => {
        if (requestOTPCodeData) {
            if (requestOTPCodeData.requestOtp.status !== "SUCCESS") {
                if (requestOTPCodeData.requestOtp.status === 'PHONE_NUMBER_EXIST') {
                    setErrorNotification(intl.formatMessage({ id: 'PHONE_NUMBER_EXIST' }));
                }
                else {
                    setErrorNotification(intl.formatMessage({ id: "FAILED" }));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestOTPCodeData]);


    useEffect(() => {
        if (submitOTPData) {
            if (submitOTPData.submitOTPVerifyPhone.status !== "SUCCESS") {
                setErrorNotification(intl.formatMessage({ id: "INVALID_PROFILE_CODE" }));
            } else {
                deleteGeneralCookie(`c-cooldown-otp`);
                setCooldown(0);
                if (cooldownTimer) {
                    clearInterval(cooldownTimer);
                }
                modalVisible.setVisible(false);
                getUserProfile();
                userPhonesRefetch();
                form.resetFields();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitOTPData]);

    useEffect(() => {
        if (submitOTPCreatePhoneData) {
            if (submitOTPCreatePhoneData.submitOTPCreatePhone.status === "CODE_NOT_MATCH") {
                setErrorNotification(intl.formatMessage({ id: "INVALID_PROFILE_CODE" }));
            } else if (submitOTPCreatePhoneData.submitOTPCreatePhone.status === "NOT_VERIFIED") {
                setErrorNotification(intl.formatMessage({ id: "ADD_PHONE_NOT_VERIFIED" }));
            } else if (submitOTPCreatePhoneData.submitOTPCreatePhone.status === "PHONE_EXIST") {
                setErrorNotification(intl.formatMessage({ id: "ERROR_PHONE_EXISTS" }));
            } else {
                deleteGeneralCookie(`c-cooldown-otp`);
                setCooldown(0);
                if (cooldownTimer) {
                    clearInterval(cooldownTimer);
                }
                modalVisible.setVisible(false);
                getUserProfile();
                userPhonesRefetch();
                form.resetFields();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitOTPCreatePhoneData]);

    //Set user profile is profile data changed
    useEffect(() => {
        if (data) {
            authDispatch({ type: SET_CURRENT_USER, payload: data });
        }// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);


    const onCloseModal = () => {
        setFirstSendCode(true);
        modalVisible.setVisible(false)
    }

    function padTo2Digits(num: number) {
        return num.toString().padStart(2, '0');
    }

    return (
        <>
            <Modal
                className="phone-modal"
                visible={modalVisible.visible}
                closable={false}
                footer={null}
                //onCancel={() => modalVisible.setVisible(false)}
                destroyOnClose
            >
                <>
                    <div className='phone-header-container'>
                        <div className="phone-title">
                            {isAddPhone ? <FormattedMessage id='PROFILE_ADD_PHONE' /> : <FormattedMessage id='PROFILE_PHONE_VERIFICATION' />}
                        </div>
                        <div className='phone-close' onClick={onCloseModal}>
                            <CloseCircleOutlined />
                        </div>
                    </div>
                    <Form
                        onFinish={onFinish}
                        form={form}
                        layout="vertical"
                        initialValues={{ phone: isAddPhone ? '' : phoneNum }}
                    >
                        <div className='phone-form-content-container'>
                            <Form.Item
                                label={isAddPhone ? intl.formatMessage({ id: 'PROFILE_NEW_PHONE' }) : intl.formatMessage({ id: 'PROFILE_VERIFY_PHONE' })}
                                name='phone'
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: 'PROFILE_PHONE_REQUIRED' }) }]}>
                                <PhoneInput selectCountryId={selectCountryId} disabled={isAddPhone ? false : true} />
                            </Form.Item>
                        </div>
                        {/* <div className='code-lbl'>
                            {isAddPhone ? <FormattedMessage id='MAIN_PHONE_FOR_VERIFICATION_CODE' values={{
                                0: mainPhoneNumber,
                            }} /> : <><FormattedMessage id='PROFILE_VERIFY_PHONE' /> <div>{phoneNum}</div></>}
                        </div> */}
                        <div className='resend-code-input'>
                            <Form.Item
                                name='otpCode'
                            // rules={[
                            //     { required: true, message: intl.formatMessage({ id: 'PROFILE_CODE_REQUIRED' }) },
                            //     { min: 6, message: intl.formatMessage({ id: 'INVALID_PROFILE_CODE' }) }
                            // ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({id:'ENTER_VERIFY_CODE'})}
                                    disabled={firstSendCode && !cooldown}
                                    maxLength={6} />
                            </Form.Item>
                            {firstSendCode && !cooldown ?
                                <div className='send-code-btn' onClick={() => { onSendCodeClick(true) }}>
                                    <div className='code-txt'><FormattedMessage id='PROFILE_SEND_CODE' /></div>
                                </div>
                                :
                                <div className='resend-code'> {cooldown ? <div>{`${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`}</div> :
                                    <div onClick={cooldown ? ()=>{} : () => { onSendCodeClick(true) }}><FormattedMessage id='PROFILE_RESEND_CODE' /></div>}</div>
                            }
                        </div>

                        <Form.Item className="form-button">
                            <Button
                                disabled={!cooldown ? true : false} onClick={() => { }}
                                type="primary"
                                htmlType="submit">
                                {<FormattedMessage id={isAddPhone ? 'BTN_CONFIRM' : 'PROFILE_VERIFY_BTN'} />}
                            </Button>
                        </Form.Item>
                    </Form>
                </>
            </Modal>
        </>
    )
}

export default ProfilePhoneModal;